/**
 * Modal para importar y exportar datos (KML/KMZ, imágenes)
 * @module components/modals/ImportExportModal
 */

import { useState, useRef, useEffect } from 'react'
import { 
  X, Upload, Download, FileText, Image, 
  CheckCircle, AlertCircle, Loader2, MapPin, 
  Cable, FileArchive, Camera, Table, FileSpreadsheet, Sheet
} from 'lucide-react'
import { useNodeStore, useFiberStore, useUIStore } from '@/stores'
import type { AnyNode, NodeType } from '@/types/nodes'

const KML_IMPORT_NODE_TYPES: NodeType[] = ['POP', 'OLT', 'NAP', 'CIERRE', 'CLIENTE', 'POSTE']

interface ImportExportModalProps {
  isOpen: boolean
  onClose: () => void
  mapContainer?: HTMLElement | null
}

type TabType = 'import' | 'import-csv' | 'export-kml' | 'export-image' | 'export-excel'

interface ImportResult {
  nodes: any[]
  cables: any[]
  errors: string[]
  warnings: string[]
}

interface CSVImportResult {
  nodes: any[]
  cables: any[]
  errors: string[]
  warnings: string[]
  headers: string[]
  totalRows: number
}

export function ImportExportModal({ isOpen, onClose, mapContainer }: ImportExportModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('import')
  const [loading, setLoading] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  /** KML/KMZ: automático según nombre o tipo fijo para todos los puntos */
  const [kmlImportTypeMode, setKmlImportTypeMode] = useState<'auto' | 'fixed'>('auto')
  const [kmlFixedNodeType, setKmlFixedNodeType] = useState<NodeType>('POSTE')
  const [exportFormat, setExportFormat] = useState<'kml' | 'kmz'>('kmz')
  const [imageFormat, setImageFormat] = useState<'png' | 'jpg'>('png')
  const [includeTitle, setIncludeTitle] = useState(true)
  const [includeLegend, setIncludeLegend] = useState(true)
  const [includeDate, setIncludeDate] = useState(true)
  const [projectName, setProjectName] = useState('Mi Red FTTH')
  const [servicesLoaded, setServicesLoaded] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  const csvFileInputRef = useRef<HTMLInputElement>(null)
  const kmlServiceRef = useRef<any>(null)
  const mapExportServiceRef = useRef<any>(null)
  const csvServiceRef = useRef<any>(null)
  const excelServiceRef = useRef<any>(null)
  const [csvResult, setCSVResult] = useState<CSVImportResult | null>(null)
  
  const nodes = useNodeStore((state) => state.nodes)
  const addNode = useNodeStore((state) => state.addNode)
  const cables = useFiberStore((state) => state.cables)
  const addCable = useFiberStore((state) => state.addCable)
  const addNotification = useUIStore((state) => state.addNotification)

  // Cargar servicios de forma dinámica cuando el modal se abre
  useEffect(() => {
    if (isOpen && !servicesLoaded) {
      const loadServices = async () => {
        try {
          const [kmlMod, mapExportMod, csvMod, excelMod] = await Promise.all([
            import('@/services/kmlService'),
            import('@/services/mapExportService'),
            import('@/services/csvImportService'),
            import('@/services/excelExportService')
          ])
          kmlServiceRef.current = kmlMod
          mapExportServiceRef.current = mapExportMod
          csvServiceRef.current = csvMod
          excelServiceRef.current = excelMod
          setServicesLoaded(true)
          setLoadError(null)
        } catch (err) {
          console.error('Error cargando servicios:', err)
          setLoadError('Error al cargar los servicios de importación/exportación')
        }
      }
      loadServices()
    }
  }, [isOpen, servicesLoaded])

  if (!isOpen) return null

  // Manejar selección de archivo
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !kmlServiceRef.current) return
    
    setLoading(true)
    setImportResult(null)
    
    try {
      const result = await kmlServiceRef.current.importKMLFile(file)
      setImportResult(result)
      
      if (result.errors.length === 0 && (result.nodes.length > 0 || result.cables.length > 0)) {
        addNotification('success', `Archivo analizado: ${result.nodes.length} nodos, ${result.cables.length} cables`)
      }
    } catch (error) {
      addNotification('error', 'Error al leer el archivo')
      setImportResult({
        nodes: [],
        cables: [],
        errors: [error instanceof Error ? error.message : 'Error desconocido'],
        warnings: [],
      })
    } finally {
      setLoading(false)
    }
  }

  // Función auxiliar para calcular distancia en metros
  const haversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371000
    const φ1 = lat1 * Math.PI / 180
    const φ2 = lat2 * Math.PI / 180
    const Δφ = (lat2 - lat1) * Math.PI / 180
    const Δλ = (lng2 - lng1) * Math.PI / 180
    const a = Math.sin(Δφ/2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ/2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  }

  // Buscar nodo más cercano a un punto
  const findClosestNode = (allNodes: AnyNode[], point: { lat: number; lng: number }, maxDistanceM: number = 100): AnyNode | null => {
    let closest: AnyNode | null = null
    let minDist = maxDistanceM
    
    for (const node of allNodes) {
      const dist = haversineDistance(point.lat, point.lng, node.lat, node.lng)
      if (dist < minDist) {
        minDist = dist
        closest = node
      }
    }
    return closest
  }

  // Confirmar importación - ahora crea nodos Y cables
  const handleConfirmImport = async () => {
    if (!importResult) return
    
    setLoading(true)
    
    try {
      let nodesAdded = 0
      let cablesAdded = 0
      let cablesSkipped = 0
      
      // 1. Primero crear todos los nodos
      for (const raw of importResult.nodes) {
        try {
          const nodeData =
            kmlImportTypeMode === 'fixed'
              ? { ...raw, tipo: kmlFixedNodeType, config: {} }
              : raw
          await addNode(nodeData as AnyNode)
          nodesAdded++
        } catch (err) {
          console.error('Error importando nodo:', err)
        }
      }
      
      // 2. Esperar un momento para que el store se actualice
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // 3. Obtener todos los nodos actualizados del store
      const currentNodes = useNodeStore.getState().nodes
      
      // 4. Crear los cables, buscando nodos cercanos para origen/destino
      for (const cableData of importResult.cables) {
        try {
          const ruta = cableData.ruta || []
          if (ruta.length < 2) {
            cablesSkipped++
            continue
          }
          
          const startPoint = ruta[0]
          const endPoint = ruta[ruta.length - 1]
          
          // Buscar nodos cercanos a los extremos del cable (tolerancia 100m)
          const origenNode = findClosestNode(currentNodes, startPoint, 100)
          const destinoNode = findClosestNode(currentNodes, endPoint, 100)
          
          if (origenNode && destinoNode && origenNode.id !== destinoNode.id) {
            // Crear cable con nodos de origen y destino
            const cableToCreate = {
              ...cableData,
              origenId: origenNode.id,
              destinoId: destinoNode.id,
              nombre: cableData.observaciones?.replace('Importado de KML: ', '') || `Cable KML ${cablesAdded + 1}`,
              codigo: `KML-${cablesAdded + 1}`,
              estado: 'ACTIVO',
            }
            
            await addCable(cableToCreate as any)
            cablesAdded++
          } else {
            // No se encontraron nodos cercanos - omitir este cable
            cablesSkipped++
          }
        } catch (err) {
          console.error('Error importando cable:', err)
          cablesSkipped++
        }
      }
      
      // Resumen de importación
      const messages: string[] = []
      if (nodesAdded > 0) messages.push(`${nodesAdded} nodos`)
      if (cablesAdded > 0) messages.push(`${cablesAdded} cables`)
      
      if (messages.length > 0) {
        addNotification('success', `Importados: ${messages.join(', ')}`)
      } else {
        addNotification('warning', 'No se importaron elementos')
      }
      
      if (cablesSkipped > 0) {
        addNotification('info', `${cablesSkipped} cables omitidos (sin nodos cercanos en extremos)`)
      }
      
      setImportResult(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      onClose()
      
    } catch (error) {
      addNotification('error', 'Error al importar los datos')
    } finally {
      setLoading(false)
    }
  }

  // Exportar KML/KMZ
  const handleExportKML = async () => {
    if (!kmlServiceRef.current) return
    
    setLoading(true)
    
    try {
      if (exportFormat === 'kmz') {
        const blob = await kmlServiceRef.current.exportToKMZ(nodes, cables, projectName)
        kmlServiceRef.current.downloadFile(blob, `${projectName.replace(/\s+/g, '_')}.kmz`)
      } else {
        const blob = kmlServiceRef.current.exportToKML(nodes, cables, projectName)
        kmlServiceRef.current.downloadFile(blob, `${projectName.replace(/\s+/g, '_')}.kml`)
      }
      
      addNotification('success', `Exportado como ${exportFormat.toUpperCase()}`)
      onClose()
    } catch (error) {
      addNotification('error', 'Error al exportar')
    } finally {
      setLoading(false)
    }
  }

  // Exportar imagen
  const handleExportImage = async () => {
    if (!mapContainer || !mapExportServiceRef.current) {
      addNotification('error', 'No se puede capturar el mapa')
      return
    }
    
    setLoading(true)
    
    try {
      const options = {
        format: imageFormat,
        quality: 0.95,
        scale: 2,
        title: includeTitle ? projectName : undefined,
        showLegend: includeLegend,
        showDate: includeDate,
      }
      
      const blob = await mapExportServiceRef.current.exportMapWithLegend(mapContainer, options)
      const filename = `${projectName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${imageFormat}`
      mapExportServiceRef.current.downloadImage(blob, filename)
      
      addNotification('success', `Imagen exportada como ${imageFormat.toUpperCase()}`)
      onClose()
    } catch (error) {
      console.error('Error exportando imagen:', error)
      addNotification('error', 'Error al exportar la imagen')
    } finally {
      setLoading(false)
    }
  }

  // Manejar selección de archivo CSV
  const handleCSVFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !csvServiceRef.current) return
    
    setLoading(true)
    setCSVResult(null)
    
    try {
      const result = await csvServiceRef.current.importCSVFile(file)
      setCSVResult(result)
      
      if (result.errors.length === 0 && (result.nodes.length > 0 || result.cables.length > 0)) {
        addNotification('success', `CSV analizado: ${result.nodes.length} nodos, ${result.cables.length} cables`)
      }
    } catch (error) {
      addNotification('error', 'Error al leer el archivo CSV')
      setCSVResult({
        nodes: [],
        cables: [],
        errors: [error instanceof Error ? error.message : 'Error desconocido'],
        warnings: [],
        headers: [],
        totalRows: 0,
      })
    } finally {
      setLoading(false)
    }
  }

  // Confirmar importación CSV
  const handleConfirmCSVImport = async () => {
    if (!csvResult) return
    
    setLoading(true)
    
    try {
      let nodesAdded = 0
      let cablesAdded = 0
      
      // Importar nodos
      for (const nodeData of csvResult.nodes) {
        try {
          await addNode(nodeData as any)
          nodesAdded++
        } catch (err) {
          console.error('Error importando nodo CSV:', err)
        }
      }
      
      // Esperar actualización del store
      if (csvResult.cables.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 500))
        const currentNodes = useNodeStore.getState().nodes
        
        // Importar cables buscando nodos por nombre
        for (const cableData of csvResult.cables) {
          try {
            const origenNode = currentNodes.find(n => 
              (n as any).nombre?.toLowerCase() === cableData.origenNombre?.toLowerCase() ||
              (n as any).codigo?.toLowerCase() === cableData.origenNombre?.toLowerCase()
            )
            const destinoNode = currentNodes.find(n => 
              (n as any).nombre?.toLowerCase() === cableData.destinoNombre?.toLowerCase() ||
              (n as any).codigo?.toLowerCase() === cableData.destinoNombre?.toLowerCase()
            )
            
            if (origenNode && destinoNode && origenNode.id !== destinoNode.id) {
              const cableToCreate = {
                tipo: cableData.tipo,
                nombre: cableData.nombre || `${cableData.tipo}-${cableData.numeroFibras}FO`,
                codigo: cableData.codigo,
                numeroFibras: cableData.numeroFibras,
                origenId: origenNode.id,
                destinoId: destinoNode.id,
                ruta: [
                  { lat: origenNode.lat, lng: origenNode.lng, tipo: 'waypoint' as const },
                  { lat: destinoNode.lat, lng: destinoNode.lng, tipo: 'waypoint' as const }
                ],
                longitudTotal: cableData.longitud || haversineDistance(
                  origenNode.lat, origenNode.lng,
                  destinoNode.lat, destinoNode.lng
                ),
                estado: 'ACTIVO',
              }
              
              await addCable(cableToCreate as any)
              cablesAdded++
            }
          } catch (err) {
            console.error('Error importando cable CSV:', err)
          }
        }
      }
      
      // Resumen
      const messages: string[] = []
      if (nodesAdded > 0) messages.push(`${nodesAdded} nodos`)
      if (cablesAdded > 0) messages.push(`${cablesAdded} cables`)
      
      if (messages.length > 0) {
        addNotification('success', `Importados desde CSV: ${messages.join(', ')}`)
      } else {
        addNotification('warning', 'No se importaron elementos')
      }
      
      setCSVResult(null)
      if (csvFileInputRef.current) csvFileInputRef.current.value = ''
      onClose()
      
    } catch (error) {
      addNotification('error', 'Error al importar datos CSV')
    } finally {
      setLoading(false)
    }
  }

  // Descargar plantilla CSV
  const handleDownloadCSVTemplate = (type: 'nodes' | 'cables') => {
    if (csvServiceRef.current) {
      csvServiceRef.current.downloadCSVTemplate(type)
      addNotification('success', `Plantilla de ${type === 'nodes' ? 'nodos' : 'cables'} descargada`)
    }
  }

  // Exportar a Excel/CSV
  const handleExportExcel = (type: 'nodes' | 'cables' | 'occupancy' | 'all') => {
    if (!excelServiceRef.current) return
    
    const options = { projectName, includeMetadata: true }
    
    try {
      switch (type) {
        case 'nodes':
          excelServiceRef.current.exportNodesToCSV(nodes, options)
          addNotification('success', 'Nodos exportados a CSV')
          break
        case 'cables':
          excelServiceRef.current.exportCablesToCSV(cables, nodes, options)
          addNotification('success', 'Cables exportados a CSV')
          break
        case 'occupancy':
          excelServiceRef.current.exportFiberOccupancyCSV(cables, options)
          addNotification('success', 'Ocupación de fibras exportada')
          break
        case 'all':
          excelServiceRef.current.exportAllToCSV(nodes, cables, options)
          addNotification('success', 'Reportes completos exportados')
          break
      }
    } catch (error) {
      addNotification('error', 'Error al exportar')
    }
  }

  const tabs = [
    { id: 'import', label: 'KML/KMZ', icon: Upload },
    { id: 'import-csv', label: 'CSV', icon: Table },
    { id: 'export-kml', label: 'KML', icon: FileText },
    { id: 'export-image', label: 'Imagen', icon: Image },
    { id: 'export-excel', label: 'CSV/Excel', icon: Sheet },
  ] as const

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-dark-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Importar / Exportar</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error de carga */}
        {loadError && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{loadError}</span>
            </div>
          </div>
        )}

        {/* Cargando servicios */}
        {!servicesLoaded && !loadError && (
          <div className="p-8 flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            <p className="text-gray-600">Cargando servicios...</p>
          </div>
        )}

        {/* Contenido principal */}
        {servicesLoaded && (
          <>
            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-dark-700 overflow-x-auto shrink-0">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-[5.5rem] shrink-0 px-3 sm:px-4 py-3 flex items-center justify-center gap-1.5 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-b-2 border-primary-500 text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-dark-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Tab: Importar */}
              {activeTab === 'import' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-lg p-8 hover:border-primary-400 transition-colors">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".kml,.kmz"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-lg font-medium">
                          Arrastra un archivo o haz clic para seleccionar
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Formatos soportados: KML, KMZ
                        </p>
                      </label>
                    </div>
                  </div>

                  {loading && (
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Procesando archivo...</span>
                    </div>
                  )}

                  {importResult && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                            <MapPin className="w-5 h-5" />
                            <span className="font-medium">{importResult.nodes.length} Nodos</span>
                          </div>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                            <Cable className="w-5 h-5" />
                            <span className="font-medium">{importResult.cables.length} Cables</span>
                          </div>
                        </div>
                      </div>

                      {importResult.errors.length > 0 && (
                        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-2">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">Errores</span>
                          </div>
                          <ul className="text-sm text-red-600 dark:text-red-300 space-y-1">
                            {importResult.errors.map((err, i) => (
                              <li key={i}>• {err}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {importResult.warnings.length > 0 && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400 mb-2">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">Advertencias</span>
                          </div>
                          <ul className="text-sm text-yellow-600 dark:text-yellow-300 space-y-1">
                            {importResult.warnings.map((warn, i) => (
                              <li key={i}>• {warn}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {importResult.nodes.length > 0 && (
                        <div className="rounded-lg border border-gray-200 dark:border-dark-600 p-4 space-y-3">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            Tipo de nodos (puntos)
                          </p>
                          <label className="flex items-start gap-2 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="kml-import-type-mode"
                              checked={kmlImportTypeMode === 'auto'}
                              onChange={() => setKmlImportTypeMode('auto')}
                              className="mt-1"
                            />
                            <span>
                              <span className="font-medium">Detección automática</span>
                              <span className="block text-gray-500 dark:text-gray-400 text-xs">
                                Según nombre y descripción del KML (comportamiento anterior)
                              </span>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="kml-import-type-mode"
                              checked={kmlImportTypeMode === 'fixed'}
                              onChange={() => setKmlImportTypeMode('fixed')}
                              className="mt-1"
                            />
                            <span>
                              <span className="font-medium">Mismo tipo para todos los puntos</span>
                              <span className="block text-gray-500 dark:text-gray-400 text-xs">
                                Útil para postes u homogeneizar marcadores de Google Earth
                              </span>
                            </span>
                          </label>
                          {kmlImportTypeMode === 'fixed' && (
                            <select
                              value={kmlFixedNodeType}
                              onChange={(e) => setKmlFixedNodeType(e.target.value as NodeType)}
                              className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg dark:bg-dark-700 text-sm"
                            >
                              {KML_IMPORT_NODE_TYPES.map((t) => (
                                <option key={t} value={t}>
                                  {t}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}

                      {importResult.nodes.length > 0 && (
                        <button
                          onClick={handleConfirmImport}
                          disabled={loading}
                          className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <CheckCircle className="w-5 h-5" />
                          {importResult.cables.length > 0
                            ? `Importar ${importResult.nodes.length} nodos y ${importResult.cables.length} cables`
                            : `Importar ${importResult.nodes.length} nodos al diseño`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Importar CSV */}
              {activeTab === 'import-csv' && (
                <div className="space-y-6">
                  {/* Zona de carga */}
                  <div className="text-center">
                    <div className="border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-lg p-8 hover:border-primary-400 transition-colors">
                      <input
                        ref={csvFileInputRef}
                        type="file"
                        accept=".csv,.txt"
                        onChange={handleCSVFileSelect}
                        className="hidden"
                        id="csv-upload"
                      />
                      <label htmlFor="csv-upload" className="cursor-pointer">
                        <Table className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-lg font-medium">
                          Importar archivo CSV
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Formatos: CSV (separado por comas, punto y coma, o tabs)
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Descargar plantillas */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-3">
                      Descargar plantillas de ejemplo:
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleDownloadCSVTemplate('nodes')}
                        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-dark-700 border rounded-lg text-sm hover:bg-gray-50"
                      >
                        <FileSpreadsheet className="w-4 h-4 text-green-600" />
                        Plantilla Nodos
                      </button>
                      <button
                        onClick={() => handleDownloadCSVTemplate('cables')}
                        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-dark-700 border rounded-lg text-sm hover:bg-gray-50"
                      >
                        <FileSpreadsheet className="w-4 h-4 text-orange-600" />
                        Plantilla Cables
                      </button>
                    </div>
                  </div>

                  {loading && (
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Procesando CSV...</span>
                    </div>
                  )}

                  {csvResult && (
                    <div className="space-y-4">
                      {/* Resumen */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{csvResult.totalRows}</p>
                          <p className="text-xs text-gray-500">Filas totales</p>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-600">{csvResult.nodes.length}</p>
                          <p className="text-xs text-blue-600">Nodos detectados</p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-green-600">{csvResult.cables.length}</p>
                          <p className="text-xs text-green-600">Cables detectados</p>
                        </div>
                      </div>

                      {/* Columnas detectadas */}
                      {csvResult.headers.length > 0 && (
                        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-500 mb-2">Columnas detectadas:</p>
                          <div className="flex flex-wrap gap-1">
                            {csvResult.headers.slice(0, 10).map((h, i) => (
                              <span key={i} className="px-2 py-0.5 bg-white dark:bg-dark-600 rounded text-xs">
                                {h}
                              </span>
                            ))}
                            {csvResult.headers.length > 10 && (
                              <span className="px-2 py-0.5 text-xs text-gray-400">
                                +{csvResult.headers.length - 10} más
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Errores */}
                      {csvResult.errors.length > 0 && (
                        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-2">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">Errores</span>
                          </div>
                          <ul className="text-sm text-red-600 dark:text-red-300 space-y-1 max-h-24 overflow-y-auto">
                            {csvResult.errors.map((err, i) => (
                              <li key={i}>• {err}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Warnings */}
                      {csvResult.warnings.length > 0 && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400 mb-2">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">Advertencias ({csvResult.warnings.length})</span>
                          </div>
                          <ul className="text-sm text-yellow-600 dark:text-yellow-300 space-y-1 max-h-24 overflow-y-auto">
                            {csvResult.warnings.slice(0, 5).map((warn, i) => (
                              <li key={i}>• {warn}</li>
                            ))}
                            {csvResult.warnings.length > 5 && (
                              <li className="text-gray-400">... y {csvResult.warnings.length - 5} más</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Botón de importación */}
                      {(csvResult.nodes.length > 0 || csvResult.cables.length > 0) && (
                        <button
                          onClick={handleConfirmCSVImport}
                          disabled={loading}
                          className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <CheckCircle className="w-5 h-5" />
                          Importar {csvResult.nodes.length > 0 ? `${csvResult.nodes.length} nodos` : ''} 
                          {csvResult.nodes.length > 0 && csvResult.cables.length > 0 ? ' y ' : ''} 
                          {csvResult.cables.length > 0 ? `${csvResult.cables.length} cables` : ''}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Exportar KML */}
              {activeTab === 'export-kml' && (
                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Datos a exportar</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <span>{nodes.length} nodos</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Cable className="w-4 h-4 text-green-500" />
                        <span>{cables.length} cables</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Nombre del proyecto</label>
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                      placeholder="Mi Red FTTH"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Formato</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setExportFormat('kmz')}
                        className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                          exportFormat === 'kmz'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-dark-600 hover:border-primary-300'
                        }`}
                      >
                        <FileArchive className="w-8 h-8 text-purple-500" />
                        <div className="text-left">
                          <p className="font-medium">KMZ</p>
                          <p className="text-xs text-gray-500">Comprimido (recomendado)</p>
                        </div>
                      </button>
                      <button
                        onClick={() => setExportFormat('kml')}
                        className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                          exportFormat === 'kml'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-dark-600 hover:border-primary-300'
                        }`}
                      >
                        <FileText className="w-8 h-8 text-blue-500" />
                        <div className="text-left">
                          <p className="font-medium">KML</p>
                          <p className="text-xs text-gray-500">XML sin comprimir</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleExportKML}
                    disabled={loading || nodes.length === 0}
                    className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Download className="w-5 h-5" />
                    )}
                    Descargar {exportFormat.toUpperCase()}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    Compatible con Google Earth, Google Maps, QGIS y otras aplicaciones GIS
                  </p>
                </div>
              )}

              {/* Tab: Exportar Imagen */}
              {activeTab === 'export-image' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nombre del proyecto</label>
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                      placeholder="Mi Red FTTH"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Formato de imagen</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setImageFormat('png')}
                        className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                          imageFormat === 'png'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-dark-600 hover:border-primary-300'
                        }`}
                      >
                        <Image className="w-8 h-8 text-blue-500" />
                        <div className="text-left">
                          <p className="font-medium">PNG</p>
                          <p className="text-xs text-gray-500">Alta calidad, transparencia</p>
                        </div>
                      </button>
                      <button
                        onClick={() => setImageFormat('jpg')}
                        className={`p-4 rounded-lg border-2 flex items-center gap-3 transition-all ${
                          imageFormat === 'jpg'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-dark-600 hover:border-primary-300'
                        }`}
                      >
                        <Camera className="w-8 h-8 text-green-500" />
                        <div className="text-left">
                          <p className="font-medium">JPG</p>
                          <p className="text-xs text-gray-500">Menor tamaño</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium">Opciones</label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeTitle}
                        onChange={(e) => setIncludeTitle(e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <span>Incluir título</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeLegend}
                        onChange={(e) => setIncludeLegend(e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <span>Incluir leyenda de colores</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeDate}
                        onChange={(e) => setIncludeDate(e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <span>Incluir fecha y marca de agua</span>
                    </label>
                  </div>

                  <button
                    onClick={handleExportImage}
                    disabled={loading || !mapContainer}
                    className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Download className="w-5 h-5" />
                    )}
                    Descargar {imageFormat.toUpperCase()}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    La imagen se generará en alta resolución (2x)
                  </p>
                </div>
              )}

              {/* Tab: Exportar Excel/CSV */}
              {activeTab === 'export-excel' && (
                <div className="space-y-6">
                  <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Datos disponibles</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <span>{nodes.length} nodos</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Cable className="w-4 h-4 text-green-500" />
                        <span>{cables.length} cables</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Sheet className="w-4 h-4 text-orange-500" />
                        <span>{cables.reduce((s, c) => s + c.numeroFibras, 0)} fibras</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Nombre del proyecto</label>
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
                      placeholder="Mi Red FTTH"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium mb-2">Exportar datos</label>
                    
                    <button
                      onClick={() => handleExportExcel('nodes')}
                      className="w-full p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-3 text-left"
                    >
                      <MapPin className="w-6 h-6 text-blue-500" />
                      <div>
                        <p className="font-medium">Exportar Nodos</p>
                        <p className="text-xs text-gray-500">Todos los nodos con coordenadas y metadata</p>
                      </div>
                      <Download className="w-5 h-5 ml-auto text-gray-400" />
                    </button>

                    <button
                      onClick={() => handleExportExcel('cables')}
                      className="w-full p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-3 text-left"
                    >
                      <Cable className="w-6 h-6 text-green-500" />
                      <div>
                        <p className="font-medium">Exportar Cables</p>
                        <p className="text-xs text-gray-500">Cables con tipo, longitud y conexiones</p>
                      </div>
                      <Download className="w-5 h-5 ml-auto text-gray-400" />
                    </button>

                    <button
                      onClick={() => handleExportExcel('occupancy')}
                      className="w-full p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-3 text-left"
                    >
                      <Sheet className="w-6 h-6 text-orange-500" />
                      <div>
                        <p className="font-medium">Ocupación de Fibras</p>
                        <p className="text-xs text-gray-500">Reporte de fibras libres vs ocupadas</p>
                      </div>
                      <Download className="w-5 h-5 ml-auto text-gray-400" />
                    </button>

                    <div className="pt-3">
                      <button
                        onClick={() => handleExportExcel('all')}
                        className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
                      >
                        <Download className="w-5 h-5" />
                        Exportar Todo (4 archivos)
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    Los archivos CSV son compatibles con Excel, Google Sheets y otras hojas de cálculo
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
